import { useState } from 'react';
import type { Skill } from '@dcc/rules/content/types.ts';
import type { StatId } from '@dcc/rules/domain/stats.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { planRollFor, type RollSelection } from '@dcc/rules/app/rollService.ts';
import { performRoll } from '@dcc/rules/app/playSessionService.ts';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';
import { newId } from '@dcc/rules/app/ids.ts';
import { buildEffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import { isDamageEntry, type CheckHistoryEntry } from '@dcc/rules/rules/rollHistory.ts';
import { dicePlusRequester } from './dicePlusRequester.ts';
import { obtainCheckDice, obtainNotationDice, totalMismatchNote } from './diceRolls.ts';
import { rerollNotation, rerollProblem, rerollWithAiFavor } from './aiFavorReroll.ts';
import type { PlaySheetController } from './usePlaySheet.ts';

/** Shared Dice+ Check execution used by the consolidated Sheet and Hotlist. */
export function useCheckRoller(play: PlaySheetController) {
  const [rolling, setRolling] = useState<string | null>(null);

  async function roll(selection: { stat: StatId; skill?: Skill | null }, mode: RollMode, forceUntrained = false): Promise<boolean> {
    const saved = await play.run('The roll could not be saved.', async (records) => {
      const effects = activeEffects(records.runtime);
      const effective = buildEffectiveCharacter(records.character, effects, (id) => contentRegistry.getItem(id));
      const normalized: RollSelection = { stat: selection.stat, skill: selection.skill ?? null };
      const plan = planRollFor(effective, normalized);
      if (forceUntrained && !plan.untrained) return 'That Skill is now trained. Choose an untrained Skill or use its normal Skill control.';
      if (!plan.ready) return plan.blockedReason ?? 'That Check cannot be rolled.';
      const input = (roller: Parameters<typeof performRoll>[0]) => performRoll(roller, {
        character: records.character,
        session: records.session,
        selection: normalized,
        mode,
        difficulty: null,
        registry: contentRegistry,
        now: new Date().toISOString(),
        newId,
        effects,
        runtime: records.runtime,
      });
      setRolling('Rolling…');
      try {
        const dice = await obtainCheckDice((roller) => {
          const result = input(roller);
          return result.ok ? result.result : null;
        }, plan.label, await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const outcome = input(dice.roller);
        if (!outcome.ok) return outcome.reason;
        return {
          character: outcome.character,
          session: outcome.session,
          runtime: outcome.runtime ?? undefined,
          note: totalMismatchNote(dice.diceTotal, outcome.result.total) ?? dice.note,
        };
      } finally {
        setRolling(null);
      }
    });
    return saved;
  }

  function rerollLatest() {
    const entry = play.records?.session.rollHistory[0] ?? null;
    if (!entry || isDamageEntry(entry)) return;
    const check = entry as CheckHistoryEntry;
    void play.run('The reroll could not be saved.', async (records) => {
      setRolling('Rerolling…');
      try {
        const dice = await obtainNotationDice(rerollNotation(check), check.dice.map(() => 20), await dicePlusRequester());
        if (!dice.ok) return dice.reason;
        const out = rerollWithAiFavor(records.character, records.session, check.id, dice.roller, new Date().toISOString());
        if (!out.ok) return out.reason;
        return {
          character: out.character,
          session: out.session,
          note: totalMismatchNote(dice.diceTotal, out.entry.total) ?? dice.note,
        };
      } finally {
        setRolling(null);
      }
    });
  }

  const latest = play.records?.session.rollHistory[0] ?? null;
  return {
    rolling,
    disabled: play.busy || rolling !== null,
    roll,
    latest,
    recent: play.records?.session.rollHistory.slice(1, 4) ?? [],
    rerollLatest,
    rerollProblem: latest && !isDamageEntry(latest) && play.records
      ? rerollProblem(play.records.character, latest)
      : null,
  };
}
